import React, { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  FileText,
  Calendar,
  Bell,
  Download,
  Info,
  Clock,
  BookOpen,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { formatCurrency } from '../../utils/formatters';

interface ComplianceItem {
  id: string;
  category: 'legal' | 'tax' | 'safety' | 'documentation' | 'deadline';
  title: string;
  description: string;
  status: 'compliant' | 'warning' | 'critical' | 'pending';
  dueDate?: Date;
  legalReference?: string;
  action?: string;
  documents?: string[];
}

interface RegulationUpdate {
  id: string;
  date: Date;
  title: string;
  description: string;
  impact: 'low' | 'medium' | 'high';
  category: string;
  source: string;
}

const LegalCompliance: React.FC = () => {
  const { portfolio, currentInputs } = useApp();
  const [selectedProperty, setSelectedProperty] = useState<number>(-1);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>([]);
  const [regulationUpdates, setRegulationUpdates] = useState<RegulationUpdate[]>([]);

  useEffect(() => {
    generateComplianceChecklist();
    loadRegulationUpdates();
  }, [selectedProperty, portfolio, currentInputs]);

  const generateComplianceChecklist = () => {
    const property = getPropertyForAnalysis();
    if (!property) return;

    const items: ComplianceItem[] = [
      // Building Safety
      {
        id: 'energy-cert',
        category: 'documentation',
        title: 'Energieausweis',
        description: 'Energieausweis muss bei Vermietung/Verkauf vorgelegt werden',
        status: Math.random() > 0.5 ? 'compliant' : 'warning',
        legalReference: 'EnEV §16',
        action: 'Energieberater beauftragen',
        documents: ['Energieausweis.pdf']
      },
      {
        id: 'fire-safety',
        category: 'safety',
        title: 'Brandschutzverordnung',
        description: 'Rauchmelder in allen Wohn- und Schlafräumen',
        status: 'compliant',
        legalReference: 'LBO §15',
        action: 'Jährliche Wartung durchführen',
        dueDate: new Date(2025, 2, 15)
      },
      {
        id: 'grundsteuer',
        category: 'tax',
        title: 'Grundsteuer-Erklärung 2025',
        description: 'Neue Grundsteuer-Feststellung ab 2025',
        status: 'pending',
        legalReference: 'GrStG §19',
        action: 'Feststellungserklärung einreichen',
        dueDate: new Date(2025, 0, 31)
      },
      {
        id: 'mietpreisbremse',
        category: 'legal',
        title: 'Mietpreisbremse Compliance',
        description: 'Maximale Miete: 110% der ortsüblichen Vergleichsmiete',
        status: property.inputs.kaltmieteProQm > 12 ? 'warning' : 'compliant',
        legalReference: 'BGB §556d',
        action: 'Mietspiegel prüfen'
      },
      {
        id: 'nebenkostenabrechnung',
        category: 'documentation',
        title: 'Nebenkostenabrechnung 2024',
        description: 'Jährliche Abrechnung bis spätestens 31.12.2025',
        status: 'pending',
        legalReference: 'BGB §556',
        dueDate: new Date(2025, 11, 31),
        action: 'Abrechnungsunterlagen sammeln'
      },
      {
        id: 'datenschutz',
        category: 'legal',
        title: 'DSGVO Compliance',
        description: 'Datenschutz bei Mieterdaten',
        status: 'compliant',
        legalReference: 'DSGVO Art. 6',
        action: 'Datenschutzerklärung aktualisieren',
        documents: ['Datenschutz_Mieter.pdf']
      },
      {
        id: 'legionellen',
        category: 'safety',
        title: 'Legionellenprüfung',
        description: 'Prüfung der Trinkwasseranlage alle 3 Jahre',
        status: 'compliant',
        legalReference: 'TrinkwV §14b',
        dueDate: new Date(2026, 5, 1),
        action: 'Prüfung beauftragen'
      },
      {
        id: 'heizkosten',
        category: 'documentation',
        title: 'Heizkostenverordnung',
        description: 'Verbrauchsabhängige Abrechnung erforderlich',
        status: 'compliant',
        legalReference: 'HeizkostenV §3',
        action: 'Messdienstleister beauftragen'
      },
      {
        id: 'weg-verwaltung',
        category: 'legal',
        title: 'WEG-Verwaltung',
        description: 'Teilnahme an Eigentümerversammlungen',
        status: 'pending',
        legalReference: 'WEG §24',
        dueDate: new Date(2025, 3, 15),
        action: 'Versammlung vorbereiten'
      },
      {
        id: 'modernisierung',
        category: 'legal',
        title: 'Modernisierungsankündigung',
        description: 'Ankündigung 3 Monate vor Beginn erforderlich',
        status: 'compliant',
        legalReference: 'BGB §555c',
        action: 'Mieter rechtzeitig informieren'
      }
    ];

    setComplianceItems(items);
  };

  const loadRegulationUpdates = () => {
    const updates: RegulationUpdate[] = [
      {
        id: '1',
        date: new Date(2025, 0, 1),
        title: 'Neue Grundsteuer tritt in Kraft',
        description: 'Bundesweite Reform der Grundsteuer mit neuen Bewertungsverfahren',
        impact: 'high',
        category: 'Steuerrecht',
        source: 'Bundesfinanzministerium'
      },
      {
        id: '2',
        date: new Date(2024, 11, 15),
        title: 'Heizungsgesetz (GEG) Änderungen',
        description: 'Neue Anforderungen für Heizungsanlagen ab 2024',
        impact: 'high',
        category: 'Energierecht',
        source: 'BMWK'
      },
      {
        id: '3',
        date: new Date(2024, 10, 1),
        title: 'Mietspiegelreform',
        description: 'Qualifizierte Mietspiegel werden Pflicht für Großstädte',
        impact: 'medium',
        category: 'Mietrecht',
        source: 'BMJ'
      },
      {
        id: '4',
        date: new Date(2024, 9, 1),
        title: 'CO2-Kostenaufteilung',
        description: 'Stufenmodell zur Aufteilung der CO2-Kosten zwischen Mieter und Vermieter',
        impact: 'medium',
        category: 'Mietrecht',
        source: 'BMWSB'
      }
    ];

    setRegulationUpdates(updates);
  };

  const getPropertyForAnalysis = () => {
    if (selectedProperty >= 0 && portfolio[selectedProperty]) {
      return portfolio[selectedProperty];
    }
    return currentInputs ? { inputs: currentInputs, calculations: null } : null;
  };

  const getComplianceScore = () => {
    const compliant = complianceItems.filter(i => i.status === 'compliant').length;
    const total = complianceItems.length;
    return total > 0 ? (compliant / total) * 100 : 0;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'text-green-600 bg-green-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'critical': return 'text-red-600 bg-red-50';
      case 'pending': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'critical': return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'pending': return <Clock className="w-5 h-5 text-blue-600" />;
      default: return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const categories = [
    { id: 'all', label: 'Alle', count: complianceItems.length },
    { id: 'legal', label: 'Rechtlich', count: complianceItems.filter(i => i.category === 'legal').length },
    { id: 'tax', label: 'Steuerlich', count: complianceItems.filter(i => i.category === 'tax').length },
    { id: 'safety', label: 'Sicherheit', count: complianceItems.filter(i => i.category === 'safety').length },
    { id: 'documentation', label: 'Dokumentation', count: complianceItems.filter(i => i.category === 'documentation').length }
  ];

  const filteredItems = activeCategory === 'all' 
    ? complianceItems 
    : complianceItems.filter(i => i.category === activeCategory);

  const upcomingDeadlines = complianceItems
    .filter(i => i.dueDate && i.dueDate > new Date())
    .sort((a, b) => (a.dueDate?.getTime() || 0) - (b.dueDate?.getTime() || 0))
    .slice(0, 5);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <Shield className="w-8 h-8 mr-3" />
              Legal Compliance Center
            </h1>
            <p className="text-indigo-100 mt-1">
              Rechtssicherheit für Ihre Immobilien - Alle Vorschriften im Blick
            </p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold">{Math.round(getComplianceScore())}%</div>
            <div className="text-sm text-indigo-200">Compliance Score</div>
          </div>
        </div>
      </div>

      {/* Property Selector */}
      {portfolio.length > 0 && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Immobilie auswählen
          </label>
          <select
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value={-1}>Aktuelle Eingabe verwenden</option>
            {portfolio.map((prop, idx) => (
              <option key={idx} value={idx}>
                {prop.inputs.propertyName || `Immobilie ${idx + 1}`} - {formatCurrency(prop.inputs.angebotspreis)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Konform</div>
              <div className="text-2xl font-bold text-green-600">
                {complianceItems.filter(i => i.status === 'compliant').length}
              </div>
            </div>
            <CheckCircle className="w-8 h-8 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Warnungen</div>
              <div className="text-2xl font-bold text-yellow-600">
                {complianceItems.filter(i => i.status === 'warning').length}
              </div>
            </div>
            <AlertTriangle className="w-8 h-8 text-yellow-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Kritisch</div>
              <div className="text-2xl font-bold text-red-600">
                {complianceItems.filter(i => i.status === 'critical').length}
              </div>
            </div>
            <AlertCircle className="w-8 h-8 text-red-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Ausstehend</div>
              <div className="text-2xl font-bold text-blue-600">
                {complianceItems.filter(i => i.status === 'pending').length}
              </div>
            </div>
            <Clock className="w-8 h-8 text-blue-200" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Compliance List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Category Tabs */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex space-x-2 overflow-x-auto">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                    activeCategory === cat.id 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.label} ({cat.count})
                </button>
              ))}
            </div>
          </div>

          {/* Compliance Items */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Compliance-Checkliste</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredItems.map(item => (
                <div key={item.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(item.status)}
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{item.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          {item.legalReference && (
                            <div className="mt-2 text-xs text-gray-500 flex items-center">
                              <BookOpen className="w-3 h-3 mr-1" />
                              {item.legalReference}
                            </div>
                          )}
                          {item.action && (
                            <div className="mt-2">
                              <span className="text-xs font-medium text-indigo-600">
                                Aktion: {item.action}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                            {item.status === 'compliant' ? 'Konform' :
                             item.status === 'warning' ? 'Warnung' :
                             item.status === 'critical' ? 'Kritisch' : 'Ausstehend'}
                          </span>
                          {item.dueDate && (
                            <div className="mt-2 text-xs text-gray-500 flex items-center justify-end">
                              <Calendar className="w-3 h-3 mr-1" />
                              {item.dueDate.toLocaleDateString('de-DE')}
                            </div>
                          )}
                        </div>
                      </div>
                      {item.documents && item.documents.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.documents.map((doc, idx) => (
                            <button
                              key={idx}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                              <FileText className="w-3 h-3 mr-1" />
                              {doc}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Upcoming Deadlines */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-indigo-600" />
              Anstehende Fristen
            </h3>
            <div className="space-y-3">
              {upcomingDeadlines.map(item => (
                <div key={item.id} className="flex items-start">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    <div className="text-xs text-gray-500">
                      {item.dueDate?.toLocaleDateString('de-DE')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Regulation Updates */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-indigo-600" />
              Gesetzesänderungen
            </h3>
            <div className="space-y-3">
              {regulationUpdates.slice(0, 3).map(update => (
                <div key={update.id} className="border-l-2 border-indigo-200 pl-3">
                  <div className="text-sm font-medium text-gray-900">{update.title}</div>
                  <div className="text-xs text-gray-600 mt-1">{update.description}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${getImpactColor(update.impact)}`}>
                      {update.impact === 'high' ? 'Hoch' :
                       update.impact === 'medium' ? 'Mittel' : 'Niedrig'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {update.date.toLocaleDateString('de-DE')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Document Generator */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4">Dokumente</h3>
            <button
              onClick={() => alert('Compliance-Report Feature wird bald verfügbar sein!')}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Compliance-Report erstellen
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900">Rechtlicher Hinweis</h4>
            <p className="text-sm text-blue-800 mt-1">
              Diese Compliance-Checkliste dient als Orientierung und ersetzt keine rechtliche Beratung. 
              Konsultieren Sie bei rechtlichen Fragen einen Fachanwalt für Miet- und Immobilienrecht.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalCompliance;